/* eslint-disable prettier/prettier */
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Grid, CircularProgress } from '@mui/material';

// project imports
import Chip from 'ui-component/extended/Chip';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

// assets
import { IconFriends, IconInbox, IconCalendar, IconTrophy, IconChartBar, IconTimeline } from '@tabler/icons';
import mixpanel from 'mixpanel-browser';

import useGroups from 'hooks/useGroups';
import useAuth from 'hooks/useAuth';
import useClubs from 'hooks/useClubs';
import useLeagues from 'hooks/useLeagues';
import GroupTabPanel from './components/GroupTabPanel';
import GroupHeader from './components/GroupHeader';

// Tab ID constants and tabMapping
import {
    TAB_FEED,
    TAB_MEMBERS,
    TAB_EVENTS,
    TAB_LEADERBOARD,
    TAB_LADDER_GRAPH,
    TAB_LEAGUE_STATS,
    TAB_MATCHES,
    TAB_SCHEDULE,
    tabMapping
} from './components/groupTabs';

function TabPanel({ children, groupTab, index, ...other }) {
    return (
        <div role="tabpanel" hidden={groupTab !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {groupTab === index && <Box sx={{ p: 0 }}>{children}</Box>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    groupTab: PropTypes.any.isRequired
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

const GroupHome = () => {
    const { subdomain } = useAuth();
    const theme = useTheme();
    const { groupId: paramGroupId, tab } = useParams();
    const { getGroupInfo, groupInfo, joinGroup, getClubGroup } = useGroups();
    const { domainClub, getClubCourts, clubCourts } = useClubs();
    const [groupId, setGroupId] = useState(paramGroupId || null);
    const [groupTab, setGroupTab] = useState(null);
    const location = useLocation();
    const { pathname } = location;
    const isGroupRoute = pathname.includes('web/group');
    const baseRoute = isGroupRoute ? `/web/group/${groupId}` : `/c`;
    const [isLoading, setLoading] = useState(true);
    const { getLeagueLeaderboard } = useLeagues();
    const [hasLeaderboard, setHasLeaderboard] = useState(false);
    const [leaderboard, setLeaderboard] = useState([]);
    const [week, setWeek] = useState();

    // Combine groupId and groupInfo fetching logic
    useEffect(() => {
        if (paramGroupId) {
            setGroupId(paramGroupId);
            getGroupInfo(paramGroupId);
        } else if (subdomain) {
            getClubGroup(subdomain);
        } else if (!paramGroupId && domainClub && domainClub.group_id) {
            setGroupId(domainClub.group_id);
            getGroupInfo(domainClub.group_id);
        }
    }, [paramGroupId, subdomain, domainClub]);

    useEffect(() => {
        if (groupId) {
            mixpanel.track('Group View', {
                group_id: groupId,
                tab
            });
        }
    }, [tab]);

    useEffect(() => {
        if (groupInfo) {
            if (groupInfo?.group?.club?.visibility === 'members-only' && !groupInfo?.group?.club?.is_member) {
                setGroupTab(TAB_SCHEDULE);
            } else if (!tab) {
                if (groupInfo?.user_group) {
                    setGroupTab(TAB_MATCHES); // set default tab to matches
                } else if (groupInfo?.group?.league) {
                    setGroupTab(TAB_LEAGUE_STATS); // league stats
                } else {
                    setGroupTab(TAB_EVENTS); // events
                }
            } else {
                setGroupTab(tabMapping[tab] || '');
            }
            setLoading(false);
        }
    }, [groupInfo, tab]);

    useEffect(() => {
        if (groupInfo?.group?.league?.id && groupTab === TAB_LEADERBOARD) {
            getLeagueLeaderboard(groupInfo.group.league.id).then((res) => {
                setHasLeaderboard(res?.has_leaderboard);
                setWeek(res?.week);
                if (res?.leaderboard) {
                    setLeaderboard(res.leaderboard.sort((a, b) => a.position - b.position));
                }
            });
        }
    }, [groupInfo?.group?.league?.id, groupTab]);

    const handleChange = (event, newTabId) => {
        setGroupTab(newTabId);
    };

    // Memoize tabOptions
    const tabOptions = useMemo(() => {
        const options = [];
        const club = groupInfo?.group?.club;

        if (club) {
            if (club?.visibility === 'members-only' && !club?.is_member) {
                options.push({
                    id: TAB_SCHEDULE,
                    to: `${baseRoute}/memberships`,
                    icon: <IconFriends stroke={1.5} size="17px" />,
                    label: 'Memberships'
                });
            } else {
                if (groupInfo?.user_group) {
                    options.push({
                        id: TAB_FEED,
                        to: `${baseRoute}/feed`,
                        icon: <IconInbox stroke={1.5} size="17px" />,
                        label: 'Feed'
                    });
                }
                options.push(
                    {
                        id: TAB_MEMBERS,
                        to: `${baseRoute}/members`,
                        icon: <IconFriends stroke={1.5} size="17px" />,
                        label: (
                            <>
                                Members <Chip label={groupInfo?.group.num_members} size="small" chipcolor="secondary" sx={{ ml: 1.5 }} />
                            </>
                        )
                    },
                    ...(clubCourts.length > 0
                        ? [
                              {
                                  id: TAB_MATCHES,
                                  to: `${baseRoute}/schedule`,
                                  icon: <IconCalendar stroke={1.5} size="17px" />,
                                  label: 'Court Reservations'
                              }
                          ]
                        : []),
                    {
                        id: TAB_EVENTS,
                        to: `${baseRoute}/events`,
                        icon: <IconTrophy stroke={1.5} size="17px" />,
                        label: 'Events'
                    }
                );
            }
        } else if (groupInfo?.user_group) {
            options.push({
                id: TAB_FEED,
                to: `${baseRoute}/feed`,
                icon: <IconInbox stroke={1.5} size="17px" />,
                label: 'Feed'
            });
        }

        if (groupInfo?.group?.league?.event_type === 'league') {
            options.push(
                {
                    id: TAB_MEMBERS,
                    to: `${baseRoute}/members`,
                    icon: <IconFriends stroke={1.5} size="17px" />,
                    label: (
                        <>
                            Players <Chip label={groupInfo?.group.num_members} size="small" chipcolor="secondary" sx={{ ml: 1.5 }} />
                        </>
                    )
                },
                {
                    id: TAB_LEADERBOARD,
                    to: `${baseRoute}/leaderboard`,
                    icon: <IconTrophy stroke={1.5} size="17px" />, // Leaderboard tab
                    label: 'Leaderboard'
                },
                {
                    id: TAB_LADDER_GRAPH,
                    to: `${baseRoute}/ladder-graph`,
                    icon: <IconTimeline stroke={1.4} size="22px" />, // Ladder Graph tab
                    label: 'Ladder Graph'
                },
                {
                    id: TAB_LEAGUE_STATS,
                    to: `${baseRoute}/league-stats`,
                    icon: <IconChartBar stroke={1.5} size="17px" />,
                    label: 'League Stats'
                },
                {
                    id: TAB_MATCHES,
                    to: `${baseRoute}/matches`,
                    icon: <IconTrophy stroke={1.5} size="17px" />,
                    label: 'Matches'
                },
                {
                    id: TAB_SCHEDULE,
                    to: `${baseRoute}/calendar`,
                    icon: <IconCalendar stroke={1.5} size="17px" />,
                    label: 'Schedule'
                }
            );
        } else if (groupInfo?.group?.league?.event_type === 'tournament') {
            options.push(
                {
                    id: TAB_MEMBERS,
                    to: `${baseRoute}/members`,
                    icon: <IconFriends stroke={1.5} size="17px" />,
                    label: (
                        <>
                            Players <Chip label={groupInfo?.group.num_members} size="small" chipcolor="secondary" sx={{ ml: 1.5 }} />
                        </>
                    )
                },
                {
                    id: TAB_LADDER_GRAPH,
                    to: `${baseRoute}/league-stats`,
                    icon: <IconCalendar stroke={1.5} size="17px" />,
                    label: 'Tournament Stats'
                },
                {
                    id: TAB_LEAGUE_STATS,
                    to: `${baseRoute}/matches`,
                    icon: <IconCalendar stroke={1.5} size="17px" />,
                    label: 'Matches'
                },
                {
                    id: TAB_MATCHES,
                    to: `${baseRoute}/calendar`,
                    icon: <IconCalendar stroke={1.5} size="17px" />,
                    label: 'Schedule'
                }
            );
        }
        return options;
    }, [groupInfo, clubCourts, baseRoute]);

    // Memoize sortedTabOptions
    const sortedTabOptions = useMemo(() => tabOptions.slice().sort((a, b) => a.id - b.id), [tabOptions]);

    useEffect(() => {
        if (!isGroupRoute && domainClub) {
            getClubCourts(domainClub?.id);
        }
    }, [isGroupRoute, domainClub]);

    return (
        <Grid container spacing={gridSpacing} data-test-id="views/groups/index">
            <Grid item xs={12}>
                <MainCard
                    contentSX={{
                        p: 1.5,
                        paddingBottom: '0px !important',
                        [theme.breakpoints.down('lg')]: {
                            textAlign: 'center'
                        }
                    }}
                >
                    <GroupHeader
                        groupInfo={groupInfo}
                        sortedTabOptions={sortedTabOptions}
                        groupTab={groupTab}
                        handleChange={handleChange}
                        isGroupRoute={isGroupRoute}
                        baseRoute={baseRoute}
                        a11yProps={a11yProps}
                        theme={theme}
                    />
                </MainCard>
            </Grid>
            <Grid item xs={12}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                        <CircularProgress />
                    </Box>
                ) : (
                    <GroupTabPanel
                        groupTab={groupTab}
                        groupId={groupId}
                        groupInfo={groupInfo}
                        isGroupRoute={isGroupRoute}
                        hasLeaderboard={hasLeaderboard}
                        week={week}
                        leaderboard={leaderboard}
                        setGroupTab={setGroupTab}
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default GroupHome;
